import { type MouseEvent } from 'react';
import { Building2, LogOut, Settings, Shield } from 'lucide-react';
import { canAccessTenantManagement, isLacAdmin } from '../../../auth/roles';
import { useAuth } from '../../../auth/useAuth';
import { useCurrentUserProfileQuery } from '../../../auth/queries/useCurrentUserProfileQuery';
import { useUserThumbnailSrc } from '../../../features/user/useUserThumbnailSrc';
import { formatInitials } from '../../../features/user/userDisplay';
import { useTenantProfileQuery } from '../../../features/tenant/queries/useTenantProfileQuery';
import { useI18n } from '../../../i18n/useI18n';
import { TenantLogo } from '../../../features/tenant/components/TenantLogo';
import { resolveTenantTabPath } from '../../../routes/tenantPaths';
import { LiksMenu, type LiksMenuItem } from '../LiksMenu/LiksMenu';

export type LiksSidebarUserMenuProps = {
  showLabels: boolean;
  showTooltips: boolean;
  isDesktopCollapsed: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onExpand: () => void;
  onNavLinkClick: () => void;
};

export function LiksSidebarUserMenu({
  showLabels,
  showTooltips,
  isDesktopCollapsed,
  isOpen,
  onOpenChange,
  onExpand,
  onNavLinkClick,
}: LiksSidebarUserMenuProps) {
  const { user, logout } = useAuth();
  const { translate } = useI18n();
  const displayName = user?.name ?? translate('layout.defaultUserName');
  const showLacMenuItem = isLacAdmin(user);
  const showTenantMenuItem = canAccessTenantManagement(user);
  const tenantQuery = useTenantProfileQuery({
    enabled: showTenantMenuItem,
  });
  const tenantDisplayName =
    tenantQuery.data?.displayName?.trim() ||
    tenantQuery.data?.name?.trim() ||
    null;
  const tenantMenuLabel =
    tenantDisplayName ?? translate('userMenu.organisationManagement');
  const currentUserProfileQuery = useCurrentUserProfileQuery(Boolean(user));
  const photoDisplaySrc = useUserThumbnailSrc(
    currentUserProfileQuery.data?.thumbnail,
  );
  const userInitials = formatInitials(displayName);

  const handleLogoutClick = () => {
    logout();
  };

  const handleUserMenuTriggerClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (isDesktopCollapsed) {
      onExpand();
      onOpenChange(true);
      return;
    }

    onOpenChange(!isOpen);
  };

  const userMenuItems: LiksMenuItem[] = [
    {
      id: 'lac',
      type: 'link',
      label: translate('userMenu.lac'),
      to: '/lac/users',
      icon: (
        <Shield className="liks-sidebar__icon" strokeWidth={2} aria-hidden />
      ),
      onClick: onNavLinkClick,
      hidden: !showLacMenuItem,
    },
    {
      id: 'tenant',
      type: 'link',
      label: tenantMenuLabel,
      to: resolveTenantTabPath(),
      icon: tenantDisplayName ? (
        <TenantLogo
          className="liks-sidebar__tenant-logo"
          logo={tenantQuery.data?.logo}
          label={tenantDisplayName}
        />
      ) : (
        <Building2 className="liks-sidebar__icon" strokeWidth={2} aria-hidden />
      ),
      onClick: onNavLinkClick,
      hidden: !showTenantMenuItem,
    },
    {
      id: 'settings',
      type: 'link',
      label: translate('userMenu.settings'),
      to: '/settings',
      icon: (
        <Settings className="liks-sidebar__icon" strokeWidth={2} aria-hidden />
      ),
      onClick: onNavLinkClick,
    },
    {
      id: 'sign-out',
      type: 'button',
      label: translate('userMenu.signOut'),
      icon: (
        <LogOut className="liks-sidebar__icon" strokeWidth={2} aria-hidden />
      ),
      className: 'liks-sidebar__link--danger',
      onClick: handleLogoutClick,
    },
  ];

  return (
    <LiksMenu
      trigger={
        <>
          <span className="liks-sidebar__avatar" aria-hidden>
            {photoDisplaySrc ? (
              <img
                className="liks-sidebar__avatar-image"
                src={photoDisplaySrc}
                alt=""
              />
            ) : (
              userInitials || 'U'
            )}
          </span>
          {showLabels && (
            <span className="liks-sidebar__user-name">{displayName}</span>
          )}
        </>
      }
      items={userMenuItems}
      placement="up"
      isOpen={showLabels && isOpen}
      onOpenChange={onOpenChange}
      onTriggerClick={handleUserMenuTriggerClick}
      showChevron={showLabels}
      toggleTooltip={showTooltips ? displayName : undefined}
      triggerClassName="liks-sidebar__user-toggle"
      itemClassName="liks-sidebar__link"
      activeItemClassName="liks-sidebar__link--active"
    />
  );
}
